import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { getGenreOptions, getProfileDefaults } from './communityUtils';
import { BADGE_ACHIEVEMENTS } from '../../../shared/achievements';
import { SUPPORTED_LANGUAGES, loadAndChangeLanguage } from '../../i18n';
import useAppStore from '../../stores/appStore';
import { getAchievementDescription, getAchievementTitle } from '../../utils/i18nHelpers';
import './Community.css';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { language: storeLanguage, setLanguage: setStoreLanguage } = useAppStore();
  const currentUser = useQuery(api.users.getCurrentUser, {});
  const updateProfile = useMutation(api.users.upsertProfile);
  const selectBadge = useMutation(api.users.selectBadge);
  const unlockedAchievements = useQuery(api.achievements.listForUser, {});
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({ ...getProfileDefaults(null), language: storeLanguage });
  const handleCheck = useQuery(
    api.users.checkHandleAvailability,
    form.handle?.trim() ? { handle: form.handle.trim() } : 'skip'
  );
  const handleTaken = handleCheck && !handleCheck.available;
  const genreOptions = getGenreOptions(t);

  useEffect(() => {
    setForm({ ...getProfileDefaults(currentUser), language: currentUser?.profile?.language || storeLanguage });
    if (currentUser?.profile?.language && currentUser.profile.language !== storeLanguage) {
      setStoreLanguage(currentUser.profile.language);
      loadAndChangeLanguage(currentUser.profile.language);
    }
  }, [currentUser]);

  const handleSave = async () => {
    setMessage('');
    setIsSaving(true);
    try {
      await updateProfile({
        displayName: form.displayName,
        handle: form.handle,
        bio: form.bio,
        genreTags: form.genreTags,
        onboardingComplete: currentUser?.profile?.onboardingComplete,
        language: form.language,
        ...(form.experienceLevel ? { experienceLevel: form.experienceLevel } : {}),
        ...(form.favoriteAuthor ? { favoriteAuthor: form.favoriteAuthor } : {}),
      });
      setMessage(t('profile.profileUpdated'));
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.message || t('profile.couldNotSave'));
    } finally {
      setIsSaving(false);
    }
  };

  const initial = (form.displayName || 'W').charAt(0);
  const handle = form.handle || 'writer';

  return (
    <div className="profile-shell">
      <div className="profile-container">
        <header className="profile-header">
          <h1>{t('profile.editProfile')}</h1>
          <div className="profile-header-links">
            <Link className="btn btn-secondary btn-sm" to="/projects/community">
              {t('community.title')}
            </Link>
            <Link className="btn btn-ghost btn-sm" to="/projects">
              {t('common.projects')}
            </Link>
          </div>
        </header>

        {message && (
          <div
            className={`community-banner ${message.includes(t('profile.profileUpdated')) ? 'success' : 'error'}`}
            onClick={() => setMessage('')}
          >
            {message}
          </div>
        )}

        <div className="profile-card">
          <div className="profile-card-banner">
            <div className="profile-card-avatar">{initial}</div>
          </div>
          <div className="profile-card-body">
            <div className="profile-card-identity">
              <h2 className="profile-card-name">{form.displayName || t('profile.yourName')}</h2>
              <p className="profile-card-handle">@{handle}</p>
            </div>

            <div className="profile-form">
              <div className="profile-row">
                <label className="profile-field">
                  <span className="profile-field-label">{t('profile.displayName')}</span>
                  <input
                    className="input"
                    value={form.displayName}
                    onChange={(e) => setForm((c) => ({ ...c, displayName: e.target.value }))}
                    placeholder={t('profile.yourPenName')}
                  />
                </label>
                <label className="profile-field">
                  <span className="profile-field-label">{t('profile.handle')}</span>
                  <input
                    className={`input ${handleTaken ? 'input-error' : ''}`}
                    value={form.handle}
                    onChange={(e) => setForm((c) => ({ ...c, handle: e.target.value }))}
                    placeholder={t('profile.username')}
                  />
                  {handleTaken && (
                    <span className="profile-field-error">{t('profile.handleTaken')}</span>
                  )}
                </label>
              </div>

              <label className="profile-field">
                <span className="profile-field-label">{t('profile.bio')}</span>
                <textarea
                  className="input textarea"
                  rows={3}
                  value={form.bio}
                  onChange={(e) => setForm((c) => ({ ...c, bio: e.target.value }))}
                  placeholder={t('profile.bioPlaceholder')}
                />
              </label>

              <div className="profile-row">
                <label className="profile-field">
                  <span className="profile-field-label">{t('profile.experience')}</span>
                  <input
                    className="input"
                    value={form.experienceLevel}
                    onChange={(e) => setForm((c) => ({ ...c, experienceLevel: e.target.value }))}
                    placeholder={t('profile.experiencePlaceholder')}
                  />
                </label>
                <label className="profile-field">
                  <span className="profile-field-label">{t('profile.favoriteAuthor')}</span>
                  <input
                    className="input"
                    value={form.favoriteAuthor}
                    onChange={(e) => setForm((c) => ({ ...c, favoriteAuthor: e.target.value }))}
                    placeholder={t('profile.favoriteAuthorPlaceholder')}
                  />
                </label>
              </div>

              <label className="profile-field">
                <span className="profile-field-label">{t('profile.language')}</span>
                <select
                  className="input"
                  value={form.language || 'en'}
                  onChange={(e) => {
                    const lang = e.target.value;
                    setForm((c) => ({ ...c, language: lang }));
                    setStoreLanguage(lang);
                    loadAndChangeLanguage(lang);
                  }}
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.nativeName}
                    </option>
                  ))}
                </select>
              </label>

              <div className="profile-field">
                <span className="profile-field-label">{t('profile.genresUpTo3')}</span>
                <div className="profile-genres">
                  {genreOptions.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`chip ${form.genreTags.includes(value) ? 'selected' : ''}`}
                      data-genre={value}
                      onClick={() =>
                        setForm((c) => ({
                          ...c,
                          genreTags: c.genreTags.includes(value)
                            ? c.genreTags.filter((g) => g !== value)
                            : [...c.genreTags, value].slice(0, 3),
                        }))
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Badge Picker */}
              <div className="profile-field">
                <span className="profile-field-label">{t('profile.displayBadge')}</span>
                <p className="profile-field-hint">{t('profile.displayBadgeHint')}</p>
                <div className="profile-badges">
                  <button
                    type="button"
                    className={`profile-badge-chip ${!currentUser?.profile?.selectedBadge ? 'selected' : ''}`}
                    onClick={async () => {
                      try { await selectBadge({ badgeId: null }); } catch {}
                    }}
                  >
                    {t('profile.noBadge')}
                  </button>
                  {BADGE_ACHIEVEMENTS.map((badge) => {
                    const unlocked = unlockedAchievements?.some((a) => a.achievementId === badge.id);
                    const isActive = currentUser?.profile?.selectedBadge === badge.id;
                    const badgeTitle = getAchievementTitle(t, badge.id, badge.title);
                    const badgeDescription = getAchievementDescription(t, badge.id, badge.description);
                    return (
                      <button
                        key={badge.id}
                        type="button"
                        className={`profile-badge-chip ${isActive ? 'selected' : ''} ${!unlocked ? 'locked' : ''}`}
                        title={unlocked ? badgeTitle : t('profile.lockedBadge', { description: badgeDescription })}
                        disabled={!unlocked}
                        onClick={async () => {
                          if (!unlocked) return;
                          try { await selectBadge({ badgeId: badge.id }); } catch {}
                        }}
                      >
                        <span className="profile-badge-icon">{badge.icon}</span>
                        <span className="profile-badge-name">{badgeTitle}</span>
                        {!unlocked && <span className="profile-badge-lock">🔒</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="profile-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isSaving || !form.displayName.trim() || handleTaken}
                >
                  {isSaving ? t('common.saving') : t('profile.saveProfile')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
